import React, { useCallback, useEffect, useState } from "react";
import clsx from "clsx";

import { Button, Hidden, Theme, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
  CheckCircle as QuoteRequestedIcon,
} from "@mui/icons-material";
import { setNestedObjectValues, useFormikContext } from "formik";
import TagManager from "react-gtm-module";
import { useLocation } from "react-router-dom";

import { QuoteConfirmDialog } from "components/QuoteConfirmDialog";
import { QUOTES } from "helpers/apis";
import useAxios from "helpers/axios";
import useCatalog from "hooks/useCatalog";
import useCatalogDetail from "hooks/useCatalogDetail";
import { DesignType, IDesign, PlatformType } from "types/design";
import { ICustomPanelForm } from "types/form";
import { IQuote } from "types/quote";

export const useStyles = makeStyles((theme: Theme) => ({
  buttonWithHiddenIcon: {
    '& span[class*="label"]': { whiteSpace: "nowrap" },
    [theme.breakpoints.only("xs")]: {
      "& .MuiButton-startIcon.MuiButton-iconSizeSmall": {
        marginRight: 0,
        marginLeft: 0,
      },
    },
    [theme.breakpoints.up("sm")]: {
      "& .MuiButton-startIcon.MuiButton-iconSizeSmall": {
        marginLeft: theme.spacing(-1),
      },
    },
  },
  flexOrder: {
    [theme.breakpoints.down('md')]: { order: 2, height: 32, minWidth: 40 },
    [theme.breakpoints.down('sm')]: { paddingLeft: 0, paddingRight: 0 },
  },
}));

interface QuoteRequestButtonProps {
  platform: PlatformType | undefined;
  requisitionId: string | undefined;
  setRequisitionId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setQuoteError: React.Dispatch<React.SetStateAction<boolean>>;
}

export const QuoteRequestButton: React.FC<QuoteRequestButtonProps> = ({
  platform,
  requisitionId,
  setRequisitionId,
  setQuoteError,
}) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isEvaluation, setIsEvaluation] = useState<boolean>(false);

  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const { pathname } = useLocation();

  const { setCustomPanels, setRowsData } = useCatalog();
  let { catalog, customize, customPanelAction, isDraftCustomPanel } =
    useCatalogDetail();
  catalog = catalog as IDesign;
  const { fetchData, loading } = useAxios({
    config: { url: QUOTES, method: "POST" },
    callOnInit: false,
  });
  const isCatalogPanel = catalog.designType === DesignType.CATALOG_PANEL;
  const { setFieldError, setSubmitting, setTouched, validateForm, values } =
    useFormikContext<ICustomPanelForm>();

  useEffect(() => {
    if (pathname.indexOf("/evaluate-panel-options") === 0) {
      setIsEvaluation(true);
      if (catalog?.requisitionId) {
        setRequisitionId(catalog?.requisitionId);
      }
    }
  }, [catalog?.requisitionId, pathname, setRequisitionId]);

  const applyRequisitionId = useCallback(
    (quote: IQuote) => {
      setRequisitionId(quote.requisitionId);
      if (isEvaluation) {
        if (quote.design) {
          setRowsData((rows: Partial<IDesign>[]) =>
            rows.map((panel: Partial<IDesign>) =>
              panel.id === quote.design
                ? { ...panel, requisitionId: quote.requisitionId }
                : panel
            )
          );
        }
        if (quote.customPanel) {
          setCustomPanels((rows: Partial<IDesign>[]) =>
            rows.map((panel: Partial<IDesign>) =>
              panel.id === quote.customPanel
                ? { ...panel, requisitionId: quote.requisitionId }
                : panel
            )
          );
        }
      }
    },
    [isEvaluation, setCustomPanels, setRequisitionId, setRowsData]
  );

  const requestQuote = (requestParams: any) => {
    fetchData(
      {
        data: {
          platformType: platform,
          ...requestParams,
        },
      },
      ({ response }: any) => {
        if (response?.data) {
          applyRequisitionId(response?.data);
          setOpenDialog(true);
        }
      }
    );
  };

  const onClick = async () => {
    if (!platform) {
      setQuoteError(true);
    } else {
      TagManager.dataLayer({
        dataLayer: {
          event: "request_quote",
          pagePath: pathname,
          pageTitle: !isEvaluation
            ? "Catalog Detail"
            : customize
            ? "Customize Evaluate Panel"
            : "View Evaluate Panel",
        },
      });

      if (!isDraftCustomPanel && (isEvaluation || !customize)) {
        requestQuote({
          [isCatalogPanel ? "design" : "customPanel"]: catalog?.id,
        });
      } else {
        const validationErrors = await validateForm();
        if (Object.keys(validationErrors).length > 0) {
          setTouched(setNestedObjectValues(validationErrors, true));
        } else {
          customPanelAction({
            values,
            actions: { setFieldError, setSubmitting },
            callback: (response: any) => {
              if (response?.data?.id) {
                requestQuote({
                  customPanel: response!.data!.id,
                });
              }
            },
          });
        }
      }
    }
  };

  const classes = useStyles();

  return (
    <>
      <Button
        startIcon={
          <Hidden smUp>
            {(!isCatalogPanel || !isEvaluation) && !!requisitionId ? (
              <QuoteRequestedIcon />
            ) : (
              <ShoppingCartOutlinedIcon />
            )}
          </Hidden>
        }
        className={clsx(classes.buttonWithHiddenIcon, classes.flexOrder)}
        fullWidth={mdUp}
        size={mdUp ? "medium" : "small"}
        onClick={onClick}
        disabled={
          loading || ((!isCatalogPanel || !isEvaluation) && !!requisitionId)
        }
      >
        <Hidden only="xs">
          {(!isCatalogPanel || !isEvaluation) && !!requisitionId
            ? "Quote requested"
            : "Request quote"}
        </Hidden>
      </Button>
      <QuoteConfirmDialog
        open={openDialog && !!requisitionId}
        onClose={() => setOpenDialog(false)}
        requisitionId={requisitionId}
        isCatalog={!isEvaluation}
      />
    </>
  );
};
